// frontend/src/components/AdminPanel.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = 'https://css-67zw.onrender.com';

export default function AdminPanel({ auth }) {
  const [adminKey, setAdminKey] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctIndex, setCorrectIndex] = useState(0); // ✅ FIXED
  const [subject, setSubject] = useState('General');
  const [explanation, setExplanation] = useState('');
  const [message, setMessage] = useState('');

  const [csvFile, setCsvFile] = useState(null);
  const [csvMessage, setCsvMessage] = useState('');

  const [mcqs, setMcqs] = useState([]);
  const [loadError, setLoadError] = useState('');

  // =========================
  // LOAD MCQs
  // =========================
  const loadMcqs = async () => {
    try {
      setLoadError('');
      const res = await axios.get(`${API_BASE}/api/mcqs?limit=200`);
      setMcqs(res.data || []);
    } catch (err) {
      console.error(err);
      setLoadError(
        'Failed to load MCQs: ' +
          (err.response?.data?.error || err.message)
      );
    }
  };

  useEffect(() => {
    loadMcqs();
  }, []);

  const handleOptionChange = (index, value) => {
    const copy = [...options];
    copy[index] = value;
    setOptions(copy);
  };

  // =========================
  // SAVE MCQ (FIXED)
  // =========================
  const saveMCQ = async () => {
    try {
      setMessage('');

      const body = {
        question,
        options,
        correctIndex: Number(correctIndex), // ✅ FIXED
        subject,
        explanation
      };

      const res = await axios.post(
        `${API_BASE}/api/mcqs?admin_key=${encodeURIComponent(adminKey)}`,
        body
      );

      setMessage(`Saved MCQ with id ${res.data._id}`);

      // reset form
      setQuestion('');
      setOptions(['', '', '', '']);
      setCorrectIndex(0);
      setSubject('General');
      setExplanation('');

      loadMcqs();
    } catch (err) {
      console.error(err);
      setMessage(
        'Error: ' + (err.response?.data?.error || err.message)
      );
    }
  };

  // =========================
  // CSV UPLOAD
  // =========================
  const uploadCSV = async () => {
    try {
      setCsvMessage('');

      if (!csvFile) {
        setCsvMessage('Please choose a CSV file first.');
        return;
      }

      const formData = new FormData();
      formData.append('file', csvFile);

      const res = await axios.post(
        `${API_BASE}/api/mcqs/upload-csv?admin_key=${encodeURIComponent(
          adminKey
        )}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setCsvMessage(
        `Uploaded successfully. Inserted ${res.data.inserted} MCQs.`
      );

      loadMcqs();
    } catch (err) {
      console.error(err);
      setCsvMessage(
        'Error: ' + (err.response?.data?.error || err.message)
      );
    }
  };

  // =========================
  // DELETE MCQ
  // =========================
  const deleteMCQ = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/mcqs/${id}`, {
        headers: auth?.token
          ? { Authorization: `Bearer ${auth.token}` }
          : {}
      });
      loadMcqs();
    } catch (err) {
      console.error(err);
      alert(
        'Delete failed: ' +
          (err.response?.data?.error || err.message)
      );
    }
  };

  if (!auth) {
    return <div style={{ padding: 20 }}>Admin login required.</div>;
  }

  // =========================
  // UI
  // =========================
  return (
    <div>
      <h2>Admin - Add MCQ / Bulk Upload</h2>

      <div style={{ marginBottom: 16 }}>
        <label>
          Admin key:&nbsp;
          <input
            type="text"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Haji"
          />
        </label>
      </div>

      <div className="mcq-card">
        <h3>Question</h3>

        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          style={{ width: '100%', minHeight: 60 }}
        />

        {options.map((opt, i) => (
          <input
            key={i}
            type="text"
            value={opt}
            onChange={(e) => handleOptionChange(i, e.target.value)}
            placeholder={`Option ${i + 1}`}
            style={{ width: '100%', marginTop: 4 }}
          />
        ))}

        <div style={{ marginTop: 8 }}>
          <label>
            Correct index (0–3):&nbsp;
            <input
              type="number"
              value={correctIndex}
              onChange={(e) =>
                setCorrectIndex(Number(e.target.value))
              }
              min={0}
              max={3}
            />
          </label>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>
            Subject:&nbsp;
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 8 }}>
          <label>
            Explanation
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              style={{ width: '100%', minHeight: 50 }}
            />
          </label>
        </div>

        <button
          style={{ marginTop: 10 }}
          onClick={saveMCQ}
          className="btn"
        >
          Save MCQ
        </button>

        {message && (
          <p
            style={{
              marginTop: 8,
              color: message.startsWith('Error')
                ? 'red'
                : 'green'
            }}
          >
            {message}
          </p>
        )}
      </div>

      <div className="mcq-card">
        <h3>Bulk CSV Upload</h3>
        <p style={{ fontSize: 14 }}>
          CSV columns:{' '}
          <code>
            question, option1, option2, option3, option4,
            correct_index, subject, explanation
          </code>
        </p>

        <input
          type="file"
          accept=".csv"
          onChange={(e) =>
            setCsvFile(e.target.files?.[0] || null)
          }
        />

        <button
          style={{ marginTop: 10 }}
          onClick={uploadCSV}
          className="btn"
        >
          Upload CSV
        </button>

        {csvMessage && (
          <p
            style={{
              marginTop: 8,
              color: csvMessage.startsWith('Error')
                ? 'red'
                : 'green'
            }}
          >
            {csvMessage}
          </p>
        )}
      </div>

      <div className="mcq-card">
        <h3>Existing MCQs</h3>

        {loadError && (
          <p style={{ color: 'red' }}>{loadError}</p>
        )}

        {mcqs.map((m, i) => (
          <div
            key={m._id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom:
                '1px solid rgba(0,0,0,0.05)'
            }}
          >
            <div style={{ flex: 1 }}>
              <strong>Q{i + 1}:</strong> {m.question}
              <div
                style={{ fontSize: 12, color: '#6b7280' }}
              >
                Subject: {m.subject}
              </div>
            </div>

            <button
              onClick={() => deleteMCQ(m._id)}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '4px 8px',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
